import axios from 'axios';
import type { AxiosResponse, AxiosInstance } from 'axios';
import { auth0, waitForAuth0Ready } from '@/main';
import { useTenantStore } from '@/stores/tenant';

const httpClient: AxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BACKEND_URL}/api`,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

httpClient.interceptors.request.use(
  async (config) => {
    if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
      return config;
    }

    try {
      // Wait for the Auth0 SDK to finish its initial session check before
      // asking for a token, otherwise the request goes out with no
      // Authorization header (-> 401).
      await waitForAuth0Ready();
      const token = await auth0.getAccessTokenSilently();
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error retrieving the Auth0 token:', error);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

httpClient.interceptors.request.use((config) => {
  const tenantStore = useTenantStore();
  if (tenantStore.selectedTenantId) {
    config.headers['x-tenant-id'] = tenantStore.selectedTenantId;
  }
  return config;
});

httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => Promise.reject(error),
);

export default httpClient;
